// src/components/FeatureGate.js
// Conditional feature gating wrapper component based on tier capabilities.

import React from "react";
import { Box, Tooltip } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useCapabilities } from "../context/CapabilitiesContext";

/**
 * FeatureGate — Renders children only if the required capability is enabled.
 * If the capability is not enabled, renders a locked placeholder or hides entirely.
 *
 * @param {Object} props
 * @param {String} props.capability - The capability flag name (e.g., "compare_export_profiles")
 * @param {React.ReactNode} props.children - Content to render if capability is enabled
 * @param {String} props.mode - "hide" (renders nothing) or "lock" (renders locked placeholder); default: "hide"
 * @param {String} props.lockedText - Text to show when locked; default: "Feature not available in your tier"
 * @param {String} props.requiredTier - Optional: name of the tier that unlocks this (e.g., "Executive")
 * @returns {React.ReactNode}
 */
export const FeatureGate = ({
  capability,
  children,
  mode = "hide",
  lockedText,
  requiredTier,
}) => {
  const { capabilities } = useCapabilities();
  const isEnabled = Boolean(capabilities?.[capability]);

  if (isEnabled) {
    return children;
  }

  const tooltipText = lockedText ||
    (requiredTier
      ? `Available on ${requiredTier} — upgrade to unlock this feature`
      : "Feature not available on your current plan");

  if (mode === "hide") {
    return null;
  }

  if (mode === "lock") {
    return (
      <Tooltip title={tooltipText}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            padding: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
            border: "1px solid #ddd",
            color: "#999",
            cursor: "not-allowed",
          }}
        >
          <LockIcon fontSize="small" />
          <span>{requiredTier ? `${requiredTier} feature` : "Locked Feature"}</span>
        </Box>
      </Tooltip>
    );
  }

  return null;
};

/**
 * TierGate — Renders children only if active tier is in the allowed list.
 * Useful for multi-tier feature gates (e.g., "pro or executive").
 *
 * @param {Object} props
 * @param {Array<String>} props.allowedTiers - List of tiers with access (e.g., ["pro", "executive"])
 * @param {React.ReactNode} props.children - Content to render if tier is allowed
 * @param {String} props.mode - "hide" or "lock"; default: "hide"
 * @param {String} props.lockedText - Text to show when locked
 * @param {String} props.requiredTier - Optional: human-readable name of the minimum required tier
 * @returns {React.ReactNode}
 */
export const TierGate = ({
  allowedTiers = [],
  children,
  mode = "hide",
  lockedText,
  requiredTier,
}) => {
  const { activeTier } = useCapabilities();
  const isAllowed = allowedTiers.includes(activeTier);

  if (isAllowed) {
    return children;
  }

  const tooltipText = lockedText ||
    (requiredTier
      ? `Available on ${requiredTier} — upgrade to unlock this feature`
      : "Feature not available on your current plan");

  if (mode === "hide") {
    return null;
  }

  if (mode === "lock") {
    return (
      <Tooltip title={tooltipText}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            padding: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
            border: "1px solid #ddd",
            color: "#999",
            cursor: "not-allowed",
          }}
        >
          <LockIcon fontSize="small" />
          <span>{requiredTier ? `${requiredTier} feature` : "Tier Locked"}</span>
        </Box>
      </Tooltip>
    );
  }

  return null;
};
